import { Button } from '@heidi/design-system/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@heidi/design-system/dropdown-menu';
import { SearchInput } from '@heidi/design-system/search-input';
import { TypographyP2 } from '@heidi/design-system/typography';
import { MoreHorizontal, Search, X } from 'lucide-react';
import type { ChangeEvent, KeyboardEvent } from 'react';
import { useIntl } from 'react-intl';
import { navV3Messages } from '../nav-v3.messages';
import {
  RECENTS_FILTER_MESSAGES,
  RECENTS_FILTER_OPTIONS,
  type RecentsFilter,
} from './recents-filter';
import { renderRecentsHeaderIconButton } from './recents-header-icon-button';

interface RecentsSectionHeaderProps {
  isSearchOpen: boolean;
  query: string;
  filter: RecentsFilter;
  onFilterChange: (next: string) => void;
  onOpenSearch: () => void;
  onCloseSearch: () => void;
  onSearchChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onSearchKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
}

export const RecentsSectionHeader = ({
  isSearchOpen,
  query,
  filter,
  onFilterChange,
  onOpenSearch,
  onCloseSearch,
  onSearchChange,
  onSearchKeyDown,
}: RecentsSectionHeaderProps) => {
  const intl = useIntl();

  return (
    <div className="flex h-8 items-center gap-1 px-2">
      {isSearchOpen ? (
        <SearchInput
          autoFocus
          density="compact"
          value={query}
          onChange={onSearchChange}
          onKeyDown={onSearchKeyDown}
          placeholder={intl.formatMessage(navV3Messages.recentsSearchPlaceholder)}
          aria-label={intl.formatMessage(navV3Messages.recentsSearchPlaceholder)}
          clearAriaLabel={intl.formatMessage(navV3Messages.recentsSearchClear)}
          // An empty field's trailing slot closes search; once there is a
          // term, SearchInput's own clear button takes the slot instead.
          accessoryEnd={
            query === '' ? (
              <Button
                type="button"
                variant="ghost"
                size="xxs"
                iconOnly
                flush="end"
                aria-label={intl.formatMessage(navV3Messages.recentsSearchClose)}
                data-testid="sidebar-recents-search-close-trigger"
                onClick={onCloseSearch}
                className="text-text-secondary"
              >
                <X aria-hidden="true" />
              </Button>
            ) : undefined
          }
          wrapperClassName="min-w-0 flex-1 gap-x-1.5 bg-transparent shadow-none"
          className="placeholder:text-text-tertiary"
          data-testid="sidebar-recents-search-input"
        />
      ) : (
        <TypographyP2
          className="min-w-0 flex-1 truncate text-text-secondary"
          data-testid="sidebar-recents-heading"
        >
          {intl.formatMessage(navV3Messages.recents)}
        </TypographyP2>
      )}
      {!isSearchOpen &&
        renderRecentsHeaderIconButton({
          icon: <Search aria-hidden="true" className="size-4" />,
          ariaLabel: intl.formatMessage(navV3Messages.searchRecents),
          testId: 'sidebar-recents-search-trigger',
          onClick: onOpenSearch,
        })}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          {renderRecentsHeaderIconButton({
            icon: <MoreHorizontal aria-hidden="true" className="size-4" />,
            ariaLabel: intl.formatMessage(navV3Messages.filterRecents),
            testId: 'sidebar-recents-filter-trigger',
          })}
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" data-testid="sidebar-recents-filter-menu">
          <DropdownMenuRadioGroup value={filter} onValueChange={onFilterChange}>
            {RECENTS_FILTER_OPTIONS.map((option) => (
              <DropdownMenuRadioItem
                key={option}
                value={option}
                data-testid={`sidebar-recents-filter-${option}`}
              >
                {intl.formatMessage(RECENTS_FILTER_MESSAGES[option])}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
